import ScrollBooster from 'scrollbooster'
import { CarouselPagination } from './carousel-pagination.js'

const mountFetchControllers = new WeakMap()

const REGION_LS_KEY = 'wyld_us_state'
// California is the only region that filters out products unavailable in-state;
// every other region shows all recommendations regardless of availability.
const CA_STATE = 'California'
// The row over-fetches recommendations (Shopify caps the API at 10) and then
// shows at most this many products.
const MAX_VISIBLE_CARDS = 4

const MOUNT_SELECTOR = '[data-cart-recommendations-mount], [data-recommendations-mount]'

const HIDDEN_CARD_CLASS = 'product-card-hidden'
const EMPTY_ROW_CLASS = 'recommended-products-row-empty'

function safeLocalStorageGet(key) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function parseIncludedStates(card) {
  const raw = card.dataset.includedStates
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function isAvailableInState(includedStates, state) {
  if (includedStates.length === 0) return true
  return includedStates.includes(state)
}

const VIEWPORT_SELECTOR = '[data-recommended-products-row-viewport]'
const TRACK_SELECTOR = '[data-recommended-products-row-track]'
const CARD_SELECTOR = '[data-product-card]'
const PREV_SELECTOR = '[data-recommended-products-row-prev]'
const NEXT_SELECTOR = '[data-recommended-products-row-next]'
const PAGINATION_SELECTOR = '[data-recommended-products-row-pagination]'
const GRAB_CLASS = 'is-grabbing'

/** `<recommended-products-row>`: a static 4-up row on desktop that becomes a
 *  ScrollBooster-powered carousel on mobile (no overflow on desktop, so the
 *  drag/arrows stay inert there). Mirrors `product-carousel.js`. The element
 *  upgrades when the fetched recommendations markup is injected into a mount. */
class RecommendedProductsRow extends HTMLElement {
  #abort
  #booster
  #viewport
  #track
  #prevBtn
  #nextBtn
  #pagination
  #resizeObserver

  connectedCallback() {
    this.#viewport = this.querySelector(VIEWPORT_SELECTOR)
    this.#track = this.querySelector(TRACK_SELECTOR)
    this.#prevBtn = this.querySelector(PREV_SELECTOR)
    this.#nextBtn = this.querySelector(NEXT_SELECTOR)
    if (!this.#viewport || !this.#track) return

    this.#abort = new AbortController()
    const { signal } = this.#abort

    // Autoplay delay (ms) is set per section via `data-slideshow-timeout`; fall
    // back to CarouselPagination's default when the attribute is missing/invalid.
    const autoplay = Number.parseInt(this.dataset.slideshowTimeout ?? '', 10) || true

    this.#pagination = new CarouselPagination(this.querySelector(PAGINATION_SELECTOR), {
      signal,
      onSelect: (x) => this.#booster?.scrollTo({ x }),
      autoplay,
      pauseOn: this,
    })

    this.#booster = new ScrollBooster({
      viewport: this.#viewport,
      content: this.#track,
      scrollMode: 'transform',
      direction: 'horizontal',
      pointerMode: 'touch',
      emulateScroll: true,
      preventDefaultOnEmulateScroll: 'horizontal',
      lockScrollOnDragDirection: 'horizontal',
      bounce: true,
      bounceForce: 0.1,
      textSelection: true,
      onPointerDown: () => this.#viewport.classList.add(GRAB_CLASS),
      onPointerUp: () => this.#viewport.classList.remove(GRAB_CLASS),
      onUpdate: (state) => this.#syncArrowState(state),
    })

    this.#prevBtn?.addEventListener('click', this.#onPrev, { signal })
    this.#nextBtn?.addEventListener('click', this.#onNext, { signal })

    this.#applyRegionFilter()
    document.addEventListener('theme:region:state-changed', this.#applyRegionFilter, { signal })

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#booster?.updateMetrics()
        this.#syncArrowState()
      })
      this.#resizeObserver.observe(this.#viewport)
      this.#resizeObserver.observe(this.#track)
    }

    this.#syncArrowState()
  }

  disconnectedCallback() {
    this.#abort?.abort()
    this.#resizeObserver?.disconnect()
    this.#pagination?.destroy()
    this.#booster?.destroy()
    this.#booster = undefined
  }

  // Cap the row to MAX_VISIBLE_CARDS; hide the whole row if none remain.
  // Only California also hides cards not available in the visitor's state —
  // every other region shows all recommendations regardless of availability.
  #applyRegionFilter = () => {
    if (!this.#track) return
    const state = safeLocalStorageGet(REGION_LS_KEY)
    const filterUnavailable = state === CA_STATE
    let shown = 0
    this.#track.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      const available = !filterUnavailable || isAvailableInState(parseIncludedStates(card), state)
      const show = available && shown < MAX_VISIBLE_CARDS
      card.classList.toggle(HIDDEN_CARD_CLASS, !show)
      if (show) shown += 1
    })
    this.classList.toggle(EMPTY_ROW_CLASS, shown === 0)
    this.#booster?.updateMetrics?.()
    this.#syncArrowState()
  }

  #onPrev = () => {
    this.#scrollByCards(-1)
  }

  #onNext = () => {
    this.#scrollByCards(1)
  }

  #scrollByCards(direction) {
    if (!this.#booster) return
    const step = this.#cardStep()
    const current = this.#booster.getState().position.x
    const max = this.#maxScroll()
    const target = Math.max(0, Math.min(max, current + direction * step))
    this.#booster.scrollTo({ x: target })
  }

  #cardStep() {
    const cards = this.#track?.querySelectorAll(CARD_SELECTOR)
    if (!cards || cards.length === 0) return this.#viewport.clientWidth
    const first = cards[0]
    const second = cards[1]
    if (second) {
      return second.getBoundingClientRect().left - first.getBoundingClientRect().left
    }
    const style = globalThis.getComputedStyle(this.#track)
    const gap = Number.parseFloat(style.columnGap || style.gap || '0') || 0
    return first.getBoundingClientRect().width + gap
  }

  #maxScroll() {
    if (!this.#viewport || !this.#track) return 0
    return Math.max(0, this.#track.scrollWidth - this.#viewport.clientWidth)
  }

  #syncArrowState(state) {
    if (!this.#booster) return
    const current = (state ?? this.#booster.getState()).position.x
    const max = this.#maxScroll()
    const atStart = current <= 1
    const atEnd = current >= max - 1
    const hasOverflow = max > 1

    if (this.#prevBtn) {
      this.#prevBtn.disabled = !hasOverflow || atStart
    }
    if (this.#nextBtn) {
      this.#nextBtn.disabled = !hasOverflow || atEnd
    }

    this.#pagination?.update({ current, max, step: this.#cardStep() })
  }
}

if (!customElements.get('recommended-products-row')) {
  customElements.define('recommended-products-row', RecommendedProductsRow)
}

function recommendationsUrlForMount(mount) {
  const host = mount.closest('[data-recommendations-url]')
  if (host?.dataset.recommendationsUrl) {
    return host.dataset.recommendationsUrl
  }
  const cartPage = mount.closest('cart-page')
  return cartPage?.dataset.recommendationsUrl ?? ''
}

function abortMountFetch(mount) {
  mountFetchControllers.get(mount)?.abort()
  mountFetchControllers.delete(mount)
}

function loadMount(mount) {
  const url = recommendationsUrlForMount(mount)
  if (!url) {
    mount.remove()
    return
  }

  abortMountFetch(mount)
  const controller = new AbortController()
  mountFetchControllers.set(mount, controller)
  const { signal } = controller

  fetch(url, { credentials: 'same-origin', signal })
    .then((response) => response.text())
    .then((text) => {
      if (signal.aborted) return
      const doc = new DOMParser().parseFromString(text, 'text/html')
      const fresh = doc.querySelector('[data-recommended-products-row]')
      if (!fresh?.querySelector(CARD_SELECTOR)) {
        mount.remove()
        return
      }
      mount.replaceChildren(document.importNode(fresh, true))
      // Let product-card.js hydrate region availability on the injected cards.
      document.dispatchEvent(new CustomEvent('theme:cards:appended'))
    })
    .catch((err) => {
      if (err?.name === 'AbortError') return
      console.error(err)
      mount.remove()
    })
    .finally(() => {
      mountFetchControllers.delete(mount)
      delete mount.dataset.recommendationsPending
      if (!signal.aborted) {
        mount.dataset.recommendationsLoaded = 'true'
      }
    })
}

function observeMount(mount) {
  if (mount.dataset.recommendationsPending === 'true') return
  if (mount.childElementCount > 0) return
  if (!recommendationsUrlForMount(mount)) {
    mount.remove()
    return
  }

  mount.dataset.recommendationsPending = 'true'
  // Fetch immediately: an empty mount has no box, so IntersectionObserver often never fires.
  loadMount(mount)
}

export function initRecommendedProductsRows() {
  document.querySelectorAll(MOUNT_SELECTOR).forEach((mount) => {
    observeMount(mount)
  })
}

document.addEventListener('theme:cart:updated', () => {
  document.querySelectorAll('cart-page [data-cart-recommendations-mount]').forEach((mount) => {
    abortMountFetch(mount)
    mount.replaceChildren()
    delete mount.dataset.recommendationsPending
    delete mount.dataset.recommendationsLoaded
  })
  initRecommendedProductsRows()
})
